/**
 * `external-sharing` Phase 6 Session 6.1 — version-graph modal.
 *
 * Visualizes the fork DAG for a share's chain as a vertical timeline of
 * conversation nodes connected by SVG edges. Driven by `useVersionGraph`,
 * which hits `GET /api/shares/:shareId/version-graph` (Phase 2 Session 2.6).
 *
 * Layout invariants:
 * - Nodes ordered by `createdAt` ASC (oldest at top).
 * - An edge renders between parent and child when `child.forkedFromShareId`
 *   matches one of `parent.childShares[].shareId`.
 * - The "current" node (the share's own conversation) is highlighted.
 * - All-revoked-children nodes are visually dimmed; their edges to children
 *   render in `text-dim` rather than the accent color.
 *
 * Diff wiring is intentional in shape: this component declares an
 * `onCompareVersions?` callback prop that fires when the user clicks an
 * edge. Session 6.2 mounts the `<VersionDiffModal>` as a sibling consumer
 * and passes the callback through. Until 6.2 lands, the callback default
 * is a noop — the edge is still clickable but does nothing visible (the
 * modal stays focused on the graph).
 *
 * Backdrop pattern matches `<SendDealLinkModal>` for visual consistency:
 * `fixed inset-0 bg-void/70 z-50` overlay + centered panel + Esc/click-outside.
 */
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { Skeleton } from '@/components/ui/Skeleton';
import { ApiError } from '@/lib/api';
import { useVersionGraph } from '@/hooks/use-version-graph';
import type { DiffSide } from '@arcanum-hq/shared-types';
import { VersionGraphNode } from './VersionGraphNode';
import { RecipientBranch } from './RecipientBranch';
import { VersionDiffModal } from './VersionDiffModal';

export interface VersionGraphModalProps {
  /** Open state controlled by the parent — caller manages mount + close. */
  open: boolean;
  /** Share id whose chain to visualize. */
  shareId: string | null;
  /** Active conversation id — gets the "You are here" treatment. */
  currentConversationId: string | null;
  /**
   * Caller's relationship to the share. Drives diff-side routing on edge
   * click — the diff endpoint requires conv-level read on `kind:'conversation'`
   * sides, and the owner doesn't have read on the recipient's fork (only on
   * their parent), while the recipient is the inverse.
   *
   * - `'owner'`: from = `conversation:<parentConvId>`, to = `frozen_state:<shareId>`.
   *   Owner sees "what I've changed since I sent the share."
   * - `'recipient'`: from = `frozen_state:<shareId>`, to = `conversation:<forkConvId>`.
   *   Recipient sees "what I've changed in my fork since the snapshot."
   *
   * Default `'recipient'` preserves pre-fix behavior.
   */
  viewerKind?: 'owner' | 'recipient';
  onClose: () => void;
  /** Fired when an edge is clicked. Session 6.2 wires `<VersionDiffModal>`. */
  onCompareVersions?: (pair: {
    fromConversationId: string;
    toConversationId: string;
  }) => void;
}

/**
 * Outer wrapper short-circuits when closed so the inner `useVersionGraph`
 * hook (and its `useQuery` call) never run for closed modals — keeps the
 * QueryClient requirement scoped to the open path. Important for callers
 * that mount the modal from a row component whose existing test suite
 * doesn't wrap in a QueryClientProvider.
 */
export function VersionGraphModal(props: VersionGraphModalProps) {
  if (!props.open) return null;
  return <VersionGraphModalBody {...props} />;
}

function VersionGraphModalBody({
  shareId,
  currentConversationId,
  viewerKind = 'recipient',
  onClose,
  onCompareVersions,
}: VersionGraphModalProps) {
  const query = useVersionGraph(shareId);
  // Edge selection carries the bridging share id alongside the
  // conversation pair — we use `kind: 'frozen_state'` for the parent end
  // (the share record is the recipient's natural access path) and
  // `kind: 'conversation'` for the child end (recipient owns the fork).
  // Pre-fix used `conversation` on both ends and 403'd because the diff
  // endpoint enforces conv-level read access on each side; share-recipient
  // access didn't elevate. e2e walkthrough Phase 6 surfaced this.
  const [diffSelection, setDiffSelection] = useState<{
    fromShareId: string;
    fromConversationId: string;
    toConversationId: string;
  } | null>(null);

  useEffect(() => {
    function onKeydown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        // Defer to inner modal: if diff is open, only close the diff.
        if (diffSelection) setDiffSelection(null);
        else onClose();
      }
    }
    window.addEventListener('keydown', onKeydown);
    return () => window.removeEventListener('keydown', onKeydown);
  }, [onClose, diffSelection]);

  function handleEdgeSelect(pair: {
    fromConversationId: string;
    toConversationId: string;
    bridgingShareId: string;
  }) {
    if (onCompareVersions) {
      onCompareVersions({
        fromConversationId: pair.fromConversationId,
        toConversationId: pair.toConversationId,
      });
      return;
    }
    setDiffSelection({
      fromShareId: pair.bridgingShareId,
      fromConversationId: pair.fromConversationId,
      toConversationId: pair.toConversationId,
    });
  }

  // Owner direction: parent conv (owner-readable) → share's frozen snapshot
  // (owner-readable). Shows "what I've changed since the share."
  // Recipient direction: share's frozen snapshot (share-readable) → fork conv
  // (recipient-readable). Shows "what I've changed in my fork."
  const diffFromSide: DiffSide | null = diffSelection
    ? viewerKind === 'owner'
      ? { kind: 'conversation', id: diffSelection.fromConversationId }
      : { kind: 'frozen_state', id: diffSelection.fromShareId }
    : null;
  const diffToSide: DiffSide | null = diffSelection
    ? viewerKind === 'owner'
      ? { kind: 'frozen_state', id: diffSelection.fromShareId }
      : { kind: 'conversation', id: diffSelection.toConversationId }
    : null;
  const diffFromTitle = diffSelection
    ? query.data?.nodes.find((n) => n.conversationId === diffSelection.fromConversationId)?.conversationTitle
    : undefined;
  const diffToTitle = diffSelection
    ? query.data?.nodes.find((n) => n.conversationId === diffSelection.toConversationId)?.conversationTitle
    : undefined;

  return (
    <div
      className="fixed inset-0 bg-void/90 backdrop-blur-sm z-50 flex items-center justify-center p-6"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      data-testid="version-graph-modal-backdrop"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="version-graph-modal-title"
        data-testid="version-graph-modal"
        className="bg-panelBg border border-panelBorder rounded-[3px] shadow-xl w-full max-w-xl max-h-[85vh] overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-panelBorder">
          <h2
            id="version-graph-modal-title"
            className="font-mono text-xs text-textBright uppercase tracking-[1px]"
          >
            Version graph
          </h2>
          <Button variant="secondary" onClick={onClose} className="text-micro">
            Close
          </Button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {query.isLoading ? (
            <div data-testid="version-graph-loading" className="flex flex-col gap-3">
              <Skeleton variant="block" className="h-16" />
              <Skeleton variant="block" className="h-16" />
              <Skeleton variant="block" className="h-16" />
            </div>
          ) : query.isError ? (
            <EmptyState
              title={
                query.error instanceof ApiError && query.error.status === 404
                  ? 'GRAPH NOT FOUND'
                  : 'COULDN\'T LOAD GRAPH'
              }
              description={
                query.error instanceof ApiError && query.error.status === 404
                  ? 'This share has no version graph available.'
                  : 'Something went wrong fetching the version graph. Try again.'
              }
              action={
                <Button variant="primary" onClick={() => query.refetch()}>
                  Retry
                </Button>
              }
            />
          ) : (() => {
            // Per-recipient tree: anchor on the original conversation
            // (root), render each childShare as a recipient branch, then
            // recurse into each branch's fork-back chain.
            const root = query.data?.nodes.find((n) => n.isOriginal) ?? null;
            if (!root) {
              return (
                <EmptyState
                  title="NO ORIGINAL CONVERSATION"
                  description="The version graph couldn't resolve a root conversation."
                />
              );
            }
            const findFork = (shareId: string) =>
              query.data?.nodes.find((n) => n.forkedFromShareId === shareId) ?? null;
            return (
              <div className="flex flex-col gap-3" data-testid="version-graph-tree">
                {/* Root conversation card */}
                <VersionGraphNode
                  node={root}
                  isCurrent={root.conversationId === currentConversationId}
                  onClose={onClose}
                />
                {root.childShares.length === 0 ? (
                  <div className="ml-4 mt-2">
                    <span className="font-mono text-xs text-text-muted italic">
                      No shares yet — Send Deal Link from the Signal tab to start.
                    </span>
                  </div>
                ) : (
                  <div className="flex flex-col" data-testid="version-graph-recipients">
                    {root.childShares.map((share) => {
                      const fork = findFork(share.shareId);
                      return (
                        <RecipientBranch
                          key={share.shareId}
                          share={share}
                          forkNode={fork}
                          isCurrent={fork?.conversationId === currentConversationId}
                          parentConversationId={root.conversationId}
                          onCompare={({ shareId, forkConversationId, parentConversationId }) =>
                            handleEdgeSelect({
                              fromConversationId: parentConversationId,
                              toConversationId: forkConversationId ?? parentConversationId,
                              bridgingShareId: shareId,
                            })
                          }
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      </div>
      {/* Inline diff modal — only mounts when an edge is clicked AND the
          parent didn't supply `onCompareVersions`. Layered z-index above the
          graph backdrop so the diff sits on top. */}
      <VersionDiffModal
        open={diffSelection !== null}
        from={diffFromSide}
        to={diffToSide}
        fromLabel={diffFromTitle}
        toLabel={diffToTitle}
        recipients={query.data?.nodes.find((n) => n.isOriginal)?.childShares ?? []}
        selectedShareId={diffSelection?.fromShareId ?? null}
        onSelectRecipient={(shareId) => {
          // Re-resolve the (parent, fork, share) triple for the newly-picked
          // recipient and re-fire handleEdgeSelect so the diff query re-runs.
          const root = query.data?.nodes.find((n) => n.isOriginal);
          if (!root) return;
          const share = root.childShares.find((s) => s.shareId === shareId);
          if (!share) return;
          const fork = query.data?.nodes.find((n) => n.forkedFromShareId === shareId) ?? null;
          handleEdgeSelect({
            fromConversationId: root.conversationId,
            toConversationId: fork?.conversationId ?? root.conversationId,
            bridgingShareId: shareId,
          });
        }}
        onClose={() => setDiffSelection(null)}
      />
    </div>
  );
}
